import React, { useEffect, useRef, useState } from 'react'
import { withStyles } from '@material-ui/core/styles'
import { Grid } from '@material-ui/core'
import { Icon } from '@iconify/react'
import { CoreButton, CoreTextField } from 'src/components/core'
import styles from './styles'

const HTML_MODES = [
  { name: 'Isolated frame', value: 'isolated', icon: 'lucide:app-window' },
  { name: 'Raw HTML', value: 'raw', icon: 'lucide:code' },
]

const Embed = props => {
  const {
    modalData,
    classes,
    onEmbed,
    initialValue = null,
    initialMode = 'isolated',
    submitLabel = 'Embed',
  } = props
  const [linkValue, setLinkValue] = useState(null)
  const [htmlValue, setHtmlValue] = useState(initialValue)
  const [htmlMode, setHtmlMode] = useState(initialMode)
  const [htmlPlaceHolder, setHtmlPlaceHolder] = useState(null)
  const [urlError, setUrlError] = useState('')
  const inputRef = useRef(null)

  const handleLinkChange = e => {
    const value = e.target.value
    setLinkValue(value)

    if (!value) {
      setUrlError('')
      return
    }

    try {
      const urlObj = new URL(value)
      if (urlObj.protocol !== 'https:') {
        setUrlError('URL must use HTTPS protocol')
        return
      }
      setUrlError('')
    } catch (e) {
      setUrlError('Invalid URL format')
    }
  }

  const handleEmbed = () => {
    if (modalData.value === 'html') {
      onEmbed(htmlValue, htmlMode)
    } else if (!urlError && linkValue) {
      onEmbed(linkValue)
    }
  }

  useEffect(() => {
    setHtmlPlaceHolder(`<h2>Your heading text goes here</h2>
      <p>Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book.</p>
    `)

    if (modalData.value !== 'html' && inputRef.current) {
      inputRef.current.focus()
    }
  }, [modalData])

  return (
    <Grid container className={classes.root}>
      <div className={classes.content}>
        <div className={classes.imageDiv}>
          <img className={classes.img} src={modalData.modalImg} alt="" />
        </div>
        <div className={classes.div}>
          <p className={classes.paragraph}>{modalData.name}</p>
        </div>
        <div className={classes.messageDiv}>
          <p className={classes.message}>
            {modalData.message}
            {modalData.value !== 'insertViaUrl' && (
              <a href={modalData.url} className={classes.link}>
                <span className={classes.span}>Learn more</span>
                <Icon width="16" height="16" icon="mynaui:external-link" />
              </a>
            )}
          </p>
        </div>
        {modalData.value === 'html' ? (
          <div className={classes.inputDivHtml}>
            <p className={classes.modeLabel}>SELECT MODE</p>
            <div className={classes.modeSwitch}>
              {HTML_MODES.map(mode => (
                <button
                  key={mode.value}
                  type="button"
                  className={
                    htmlMode === mode.value
                      ? `${classes.modeButton} ${classes.modeButtonActive}`
                      : classes.modeButton
                  }
                  onClick={() => setHtmlMode(mode.value)}
                >
                  <Icon width="16" height="16" icon={mode.icon} />
                  <span>{mode.name}</span>
                </button>
              ))}
            </div>
            <div
              className={
                htmlMode === 'isolated'
                  ? `${classes.modeInfo} ${classes.modeInfoIsolated}`
                  : `${classes.modeInfo} ${classes.modeInfoRaw}`
              }
            >
              {htmlMode === 'isolated' ? (
                <p className={classes.modeInfoText}>
                  <strong>The content is isolated.</strong> This block's CSS and
                  JS cannot reach the rest of the page, and the page styles
                  cannot leak in. This is the right choice for embedded content
                  such as videos, forms and calculators.
                </p>
              ) : (
                <p className={classes.modeInfoText}>
                  <strong>The content goes straight into the page.</strong>{' '}
                  <code className={classes.modeInfoCode}>&lt;style&gt;</code>{' '}
                  rules in this mode affect the whole page, not just this block.
                  Use it only when you deliberately want to style the page
                  itself.
                </p>
              )}
            </div>
            <CoreTextField
              value={htmlValue}
              onChange={e => setHtmlValue(e.target.value)}
              size="small"
              multiline
              minRows={7}
              class={
                htmlMode === 'raw'
                  ? classes.textArea
                  : `${classes.textArea} ${classes.textAreaIsolated}`
              }
              placeholder={htmlPlaceHolder}
              fullWidth
            />
            <div className={classes.buttonHtml}>
              <CoreButton
                color="primary"
                size="small"
                onClick={handleEmbed}
                disabled={!htmlValue}
              >
                {submitLabel}
              </CoreButton>
            </div>
          </div>
        ) : (
          <div className={classes.inputDiv}>
            <CoreTextField
              value={linkValue}
              onChange={handleLinkChange}
              size="small"
              placeholder="Paste link here"
              fullWidth
              inputRef={inputRef}
              error={!!urlError}
              helperText={urlError}
              onKeyDown={e => {
                if (e.key === 'Enter') handleEmbed()
              }}
            />
            <div className={classes.buttonDiv}>
              <CoreButton
                color="primary"
                size="small"
                onClick={handleEmbed}
                disabled={!linkValue || !!urlError}
              >
                Embed
              </CoreButton>
            </div>
          </div>
        )}
      </div>
    </Grid>
  )
}

export default withStyles(styles, { withTheme: true })(Embed)
